import { AuthenticationDetails, CognitoUser, CognitoUserPool, } from 'amazon-cognito-identity-js';
import { getCognitoConfig } from '../config/cognito';
let userPool = null;
function getUserPool() {
    if (!userPool) {
        const { userPoolId, clientId } = getCognitoConfig();
        userPool = new CognitoUserPool({
            UserPoolId: userPoolId,
            ClientId: clientId,
        });
    }
    return userPool;
}
function parseIdToken(idToken) {
    const payload = JSON.parse(atob(idToken.split('.')[1]));
    return {
        email: payload.email ?? '',
        name: payload.name ?? payload.email ?? 'User',
        sub: payload.sub,
    };
}
const SESSION_KEY = 'wabta_portal_id_token';
const ACCESS_TOKEN_KEY = 'wabta_portal_access_token';
/** Refresh a minute early so in-flight calls do not race expiry. */
const TOKEN_EXPIRY_SKEW_MS = 60000;
let refreshInFlight = null;
function clearStoredTokens() {
    sessionStorage.removeItem(SESSION_KEY);
    sessionStorage.removeItem(ACCESS_TOKEN_KEY);
}
function persistSession(session) {
    const idToken = session.getIdToken().getJwtToken();
    sessionStorage.setItem(SESSION_KEY, idToken);
    sessionStorage.setItem(ACCESS_TOKEN_KEY, session.getAccessToken().getJwtToken());
    return idToken;
}
function isJwtExpiredOrNearExpiry(token, skewMs = TOKEN_EXPIRY_SKEW_MS) {
    try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        if (typeof payload.exp !== 'number')
            return true;
        return payload.exp * 1000 < Date.now() + skewMs;
    }
    catch {
        return true;
    }
}
function getStoredUser() {
    const idToken = sessionStorage.getItem(SESSION_KEY);
    if (!idToken)
        return null;
    try {
        if (isJwtExpiredOrNearExpiry(idToken, 0)) {
            return null;
        }
        return parseIdToken(idToken);
    }
    catch {
        clearStoredTokens();
        return null;
    }
}
export function getIdToken() {
    return sessionStorage.getItem(SESSION_KEY);
}
export function getAccessToken() {
    return sessionStorage.getItem(ACCESS_TOKEN_KEY);
}
/**
 * Return a fresh id token, refreshing via Cognito refresh token when expired
 * or within the skew window. Dedupes concurrent refresh attempts.
 */
export function ensureValidIdToken() {
    const current = sessionStorage.getItem(SESSION_KEY);
    if (current && !isJwtExpiredOrNearExpiry(current)) {
        return Promise.resolve(current);
    }
    if (refreshInFlight) {
        return refreshInFlight;
    }
    refreshInFlight = new Promise((resolve) => {
        const cognitoUser = getUserPool().getCurrentUser();
        if (!cognitoUser) {
            clearStoredTokens();
            resolve(null);
            return;
        }
        cognitoUser.getSession((err, session) => {
            if (err || !session?.isValid()) {
                clearStoredTokens();
                resolve(null);
                return;
            }
            resolve(persistSession(session));
        });
    }).finally(() => {
        refreshInFlight = null;
    });
    return refreshInFlight;
}
export async function getValidAuthorizationHeader() {
    const token = (await ensureValidIdToken()) ?? getAccessToken();
    if (!token) {
        throw new Error('No bearer token available. Please sign in again.');
    }
    return `Bearer ${token}`;
}
export function getCurrentSession() {
    return ensureValidIdToken().then((token) => {
        if (!token) {
            return getStoredUser();
        }
        try {
            return parseIdToken(token);
        }
        catch {
            clearStoredTokens();
            return null;
        }
    });
}
/** Held between authenticateUser(newPasswordRequired) and completeNewPassword. */
let pendingNewPasswordUser = null;
let pendingNewPasswordAttributes = {};
function clearNewPasswordChallenge() {
    pendingNewPasswordUser = null;
    pendingNewPasswordAttributes = {};
}
/**
 * Sign in with email/password. Admin-created users with a temporary password
 * resolve as `newPasswordRequired` — call `completeNewPassword` next.
 */
export function signIn(email, password) {
    clearNewPasswordChallenge();
    const pool = getUserPool();
    const username = email.trim();
    const authDetails = new AuthenticationDetails({
        Username: username,
        Password: password,
    });
    const cognitoUser = new CognitoUser({
        Username: username,
        Pool: pool,
    });
    return new Promise((resolve, reject) => {
        cognitoUser.authenticateUser(authDetails, {
            onSuccess: (session) => {
                const token = persistSession(session);
                resolve({ status: 'authenticated', user: parseIdToken(token) });
            },
            onFailure: (err) => reject(err),
            newPasswordRequired: (userAttributes) => {
                // Cognito rejects these if echoed back on completeNewPasswordChallenge.
                const attrs = { ...userAttributes };
                delete attrs.email;
                delete attrs.email_verified;
                delete attrs.phone_number_verified;
                pendingNewPasswordUser = cognitoUser;
                pendingNewPasswordAttributes = attrs;
                resolve({ status: 'newPasswordRequired', email: username });
            },
        });
    });
}
/** Finish first-login challenge after `signIn` returned `newPasswordRequired`. */
export function completeNewPassword(newPassword) {
    const cognitoUser = pendingNewPasswordUser;
    if (!cognitoUser) {
        return Promise.reject(new Error('No pending new-password challenge. Sign in again.'));
    }
    const attributes = { ...pendingNewPasswordAttributes };
    return new Promise((resolve, reject) => {
        cognitoUser.completeNewPasswordChallenge(newPassword, attributes, {
            onSuccess: (session) => {
                clearNewPasswordChallenge();
                const token = persistSession(session);
                resolve(parseIdToken(token));
            },
            onFailure: (err) => reject(err),
        });
    });
}
export function cancelNewPasswordChallenge() {
    clearNewPasswordChallenge();
}
export function signOut() {
    clearNewPasswordChallenge();
    clearStoredTokens();
    const pool = getUserPool();
    pool.getCurrentUser()?.signOut();
}
export function parseHashTokens() {
    const hash = window.location.hash.substring(1);
    if (!hash)
        return null;
    const params = new URLSearchParams(hash);
    const idToken = params.get('id_token');
    if (!idToken)
        return null;
    sessionStorage.setItem(SESSION_KEY, idToken);
    const accessToken = params.get('access_token');
    if (accessToken) {
        sessionStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
    }
    window.history.replaceState(null, '', window.location.pathname);
    return parseIdToken(idToken);
}
